import {
  useQuery,
  GET_HEADER_PREFERENCES_NAME,
  GET_ADVISOR_CLIENT_HEADER_PREFERNCES_NAME,
  User,
  AdvisorListRecord,
  useUpdateUserObservablesMutation,
  useGetEmulationUserAdvisorListImagesQuery,
  useGetUserAdvisorListImagesQuery,
  ApolloError,
  useGetClientAndHomeDataLazyQuery,
  GetClientAndHomeDataQuery,
  useTrackLoginMutation,
  TrackLoginMutationFn,
  useBillManagerSoapResponseMutation,
  FetchResult,
  BillManagerSoapResponseMutation,
  AdvisorListItemUnion,
  useFragment,
  Complete_User_Advisor_List_Item_FragmentFragmentDoc,
  Maybe,
  useGetEmulationNotificationsQuery,
  useGetClientNotificationsQuery,
  useUpdateElecDeliveryMutation,
  UpdateElecDeliveryInput,
  UpdateElecDeliveryMutation,
  useErrorLogger,
} from '@bessemercws/graphql';
import { ClientProviderStateObservables, useClientContext } from '@bessemercws/providers';

import { mapUserObservables } from '#/mappers';

interface UseAdvisorListFragment {
  isComplete: boolean;
  advisors: AdvisorListItemUnion[];
}

interface UseGraphqlServiceInterface {
  getHeaderData: (userId?: string) => { data: User; loading: boolean };
  useGetMyTeamListImages: (clientId?: string) => {
    data: AdvisorListRecord[] | null;
    loading: boolean;
    error: ApolloError | undefined;
  };
  useAdvisorListFragment: (clientId?: Maybe<string>) => UseAdvisorListFragment;
  /**
   * Mutation that returns the redirect url inside the body of a SOAP response string given a billMangerId.
   * @param billManagerId
   * @returns SOAP string from FIS
   */
  getBillManager: () => [
    (billManagerId: string) => Promise<FetchResult<BillManagerSoapResponseMutation> | null>,
    { data: string | undefined; loading: boolean; error: ApolloError | undefined },
  ];

  updateUserObservables: (observables: ClientProviderStateObservables) => Promise<void>;

  /**
   * lazy query to fetch client and homepage data
   * @param userId - selected userId, only populated when isAdvisor
   */
  lazyPreloadClientHomeData: () => Promise<GetClientAndHomeDataQuery>;
  trackLogin: TrackLoginMutationFn;

  /**
   * function to get notification list
   */
  useNotificationList: () =>
    | ({
        __typename?: 'UserNotificationMessage';
        body?: string | null;
        date?: string | null;
        isNew?: boolean | null;
        messageId: string;
        title?: string | null;
        sourceFile?: string | null;
      } | null)[]
    | null
    | undefined;

  /**
   * update function for first time electronic delivery preferences selection
   */
  updateElecDelivery(input: UpdateElecDeliveryInput): Promise<UpdateElecDeliveryMutation | null>;
}

export const useGraphqlService = (): UseGraphqlServiceInterface => {
  const { isAdvisor, idToken, selectedClient } = useClientContext();
  const logError = useErrorLogger();

  const [trackLoginFn] = useTrackLoginMutation({
    onError: logError,
  });
  const [updateUserObservablesMutation] = useUpdateUserObservablesMutation({
    onError: logError,
  });
  const [updateElecDeliveryFn] = useUpdateElecDeliveryMutation({
    onError: logError,
  });

  const [billManagerFn, { data: billManagerResponseData, loading: billManagerLoading, error: billManagerError }] =
    useBillManagerSoapResponseMutation({
      onError: logError,
    });

  async function updateUserObservables(observables: ClientProviderStateObservables) {
    const input = mapUserObservables(observables);
    await updateUserObservablesMutation({ variables: { input }, onError: logError });
  }

  const getBillManager = (): [
    (billManagerId: string) => Promise<FetchResult<BillManagerSoapResponseMutation> | null>,
    { data: string | undefined; loading: boolean; error: ApolloError | undefined },
  ] => {
    return [
      (billManagerId: string) =>
        billManagerFn({ variables: { billManagerId }, context: { clientName: 'web' }, onError: logError }),
      { data: (billManagerResponseData as string) ?? '', loading: billManagerLoading, error: billManagerError },
    ];
  };

  function getHeaderData(userId?: string): { data: User; loading: boolean } {
    const { data: userData, loading: userLoading } = useQuery(GET_HEADER_PREFERENCES_NAME, {
      skip: isAdvisor,
      onError: logError,
    });

    const { data: advisorData, loading: advisorLoading } = useQuery(GET_ADVISOR_CLIENT_HEADER_PREFERNCES_NAME, {
      skip: !isAdvisor || !userId,
      variables: { userId },
      context: { clientId: userId },
      onError: logError,
    });

    const data = isAdvisor ? advisorData?.advisor?.client : userData?.viewer;
    const loading = Boolean(userLoading || advisorLoading);
    return { data, loading };
  }

  function useGetMyTeamListImages(clientId?: string): {
    data: AdvisorListRecord[] | null;
    loading: boolean;
    error: ApolloError | undefined;
  } {
    const {
      data: clientData,
      loading: clientLoading,
      error: clientError,
    } = useGetUserAdvisorListImagesQuery({
      skip: isAdvisor || !!clientId,
      onError: logError,
    });

    const {
      data: advisorData,
      loading: advisorLoading,
      error: advisorError,
    } = useGetEmulationUserAdvisorListImagesQuery({
      skip: !isAdvisor || !clientId,
      variables: { userId: clientId || '' },
      context: { clientId },
      onError: logError,
    });

    const data = isAdvisor ? advisorData?.advisor?.client.advisorList?.items : clientData?.viewer?.advisorList?.items;
    const loading = isAdvisor ? advisorLoading : clientLoading;
    const error = isAdvisor ? advisorError : clientError;

    return { data: data as AdvisorListRecord[], loading, error };
  }

  const useAdvisorListFragment = (clientId?: Maybe<string>): UseAdvisorListFragment => {
    const { data, complete } = useFragment({
      fragment: Complete_User_Advisor_List_Item_FragmentFragmentDoc,
      from: { __ref: `AdvisorListNode:${clientId}` },
    });

    return {
      advisors: data.items,
      isComplete: complete,
    };
  };

  const [clientHomepagePreloadPreloadFn] = useGetClientAndHomeDataLazyQuery({
    errorPolicy: 'ignore',
    onError: logError,
  });
  const lazyPreloadClientHomeData = (): Promise<GetClientAndHomeDataQuery> => {
    return new Promise(
      (resolve) =>
        idToken &&
        clientHomepagePreloadPreloadFn({
          onCompleted: (data) => resolve(data),
          onError: (error) => logError(error),
        }),
    );
  };

  const useNotificationList = () => {
    const emulationResponse = useGetEmulationNotificationsQuery({
      variables: {
        userId: selectedClient || '',
      },
      skip: !isAdvisor || !selectedClient,
      onError: logError,
    });

    const clientResponse = useGetClientNotificationsQuery({
      skip: isAdvisor,
      onError: logError,
    });

    if (isAdvisor) {
      return emulationResponse?.data?.advisor?.client?.notifications?.notifications;
    } else {
      return clientResponse?.data?.viewer?.notifications?.notifications;
    }
  };

  async function updateElecDelivery(input: UpdateElecDeliveryInput): Promise<UpdateElecDeliveryMutation | null> {
    return (
      (
        await updateElecDeliveryFn({
          variables: { input },
          onError: logError,
          update(cache, { data }) {
            cache.modify({
              id: `User:${data?.updateElectronicDelivery.userId}`,
              fields: {
                electronicDelivery(currUserElecDelivery) {
                  return data?.updateElectronicDelivery.electionDesc ?? currUserElecDelivery;
                },
                verifiedDeliveryOption() {
                  return data?.updateElectronicDelivery.electionDesc;
                },
              },
            });
          },
        })
      )?.data ?? null
    );
  }

  return {
    getHeaderData,
    useGetMyTeamListImages,
    useAdvisorListFragment,
    updateUserObservables,
    lazyPreloadClientHomeData,
    getBillManager: getBillManager,
    trackLogin: trackLoginFn,
    useNotificationList,
    updateElecDelivery,
  };
};
